import { useRef, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { usePushRules, PushRuleState } from '../hooks/usePushRules';
import { useI18n } from '../context/I18nContext';
import useClickOutside from '../hooks/useClickOutside';
import logger from '../utils/logger';
import './ConversationContextMenu.css';

/**
 * Context menu component for conversation list items.
 * Provides quick actions like mute/unmute notifications and access to room settings.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the menu is visible
 * @param {Object} props.position - The x,y coordinates for menu positioning
 * @param {string} props.roomId - The Matrix room ID
 * @param {boolean} props.isMuted - Whether the conversation is currently muted
 * @param {Function} props.onClose - Callback when the menu should close
 * @param {Function} [props.onOpenSettings] - Optional callback to open room settings
 */
const ConversationContextMenu = ({ isOpen, position, roomId, isMuted, onClose, onOpenSettings }) => {
  const { t } = useI18n();
  const { setRoomState } = usePushRules();
  const menuRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  // Use the existing useClickOutside hook for click-outside handling
  useClickOutside(menuRef, onClose, isOpen);

  // Adjust position to prevent viewport overflow
  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current) {
      setAdjustedPosition(position);
      return;
    }

    const menu = menuRef.current;
    const rect = menu.getBoundingClientRect();
    const menuWidth = rect.width || 180; // fallback to min-width
    const menuHeight = rect.height || 100;
    const buffer = 8;

    let newX = position.x;
    let newY = position.y;

    // Check right edge
    if (position.x + menuWidth > window.innerWidth - buffer) {
      newX = window.innerWidth - menuWidth - buffer;
    }

    // Check left edge
    if (newX < buffer) {
      newX = buffer;
    }

    // Check bottom edge
    if (position.y + menuHeight > window.innerHeight - buffer) {
      newY = position.y - menuHeight; // Open upward
    }

    // Check top edge
    if (newY < buffer) {
      newY = buffer;
    }

    setAdjustedPosition({ x: newX, y: newY });
  }, [isOpen, position]);

  const handleMuteToggle = async () => {
    try {
      const newState = isMuted ? PushRuleState.NOTIFY : PushRuleState.DONT_NOTIFY;
      await setRoomState(roomId, newState);
    } catch (error) {
      logger.error('[ConversationContextMenu] Failed to toggle mute:', error);
    } finally {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="conversation-context-menu"
      style={{ left: adjustedPosition.x, top: adjustedPosition.y }}
    >
      <button onClick={handleMuteToggle}>
        <ion-icon name={isMuted ? 'notifications-outline' : 'notifications-off-outline'} />
        {isMuted ? t('ConversationContextMenu.unmuteConversation') : t('ConversationContextMenu.muteConversation')}
      </button>
      {onOpenSettings && (
        <button onClick={() => { onOpenSettings(); onClose(); }}>
          <ion-icon name="settings-outline" />
          {t('ConversationContextMenu.roomSettings')}
        </button>
      )}
    </div>
  );
};

ConversationContextMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }).isRequired,
  roomId: PropTypes.string.isRequired,
  isMuted: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenSettings: PropTypes.func,
};

export default ConversationContextMenu;
